/**
 * 튜토리얼 오버레이 — Phaser 뷰. 화면을 어둡게 깔고 강조 영역(컷아웃)만 비우며 메시지 박스를 띄운다.
 *
 * 렌더 전략(마스크 불필요): 컷아웃 사각형 **둘레 4장의 dim Rectangle**로 구멍을 만든다(타겟 없으면 전체 1장).
 * 입력은 진행 모드에 따라 다르게 처리한다:
 * - `tap`         : 전면 Zone — 어디를 탭하든 advance.
 * - `spotlightTap`: 전면 Zone — 컷아웃 내부 탭만 advance.
 * - `action`      : 전면 Zone 없음 — 컷아웃(실제 UI) 입력을 통과시키고, dim 영역만 탭을 흡수. advance는 `notifyAction`으로.
 *
 * 메시지 박스 스타일(`panelStyle`)·폰트·색은 호출자(앱)가 주입한다 — 패키지가 특정 게임 에셋을 모르게 하기 위함(DIP).
 */
import Phaser from "phaser";
import {
  NineSlicePanel,
  InlineIconText,
  type PanelStyleToken,
  type InlineIconSpec,
} from "@yjsgames/phaser-ui";
import type { TutorialRect } from "./types.js";
import type {
  TutorialAdvanceMode,
  TutorialMessagePosition,
  TutorialGesture,
} from "./types.js";
import type { TutorialTranslate } from "./ports.js";
import { TutorialPointer } from "./TutorialPointer.js";
import { createEnteringGestureGate } from "./advanceGate.js";
import { computeMessagePanelY } from "./messagePanelLayout.js";
import { drawTutorialSkipButton } from "./TutorialSkipButton.js";
import { computeCutout, pointInRect, resolveTextDirection, type TutorialTextDirection } from "./TutorialOverlayGeometry.js";
import type { TutorialSafeAreaInsets, TutorialSkipButtonAnchorRect } from "./TutorialSkipButtonLayout.js";

export interface TutorialOverlayOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  /** 메시지 박스 9-slice 스타일(앱 uiTokens 주입). */
  readonly panelStyle: PanelStyleToken;
  /** 텍스트 폰트 family(앱 주입). */
  readonly fontFamily: string;
  /** 로컬라이즈 함수. */
  readonly translate: TutorialTranslate;
  /** 진행 콜백(탭으로 advance 시 호출). */
  readonly onAdvance: () => void;
  /** 스킵 콜백. 주입되면 현재 튜토리얼을 완료 처리하고 닫는 버튼을 표시한다. */
  readonly onSkip?: () => void;
  /** 스킵 버튼 라벨 i18n key(기본 tutorial.skip). */
  readonly skipLabelKey?: string;
  /** world 좌표계 safe-area inset. 스킵 버튼 등 화면 고정 UI 배치에 사용한다. */
  readonly safeAreaInsets?: TutorialSafeAreaInsets;
  /** 오버레이 Container의 렌더 깊이(기본 1500). */
  readonly depth?: number;
  /** dim 사각형 색(기본 0x05060f). */
  readonly dimColor?: number;
  /** dim 사각형 불투명도(기본 0.74). */
  readonly dimAlpha?: number;
  /** 컷아웃 하이라이트 테두리 색(기본 0x5ff0ff). */
  readonly highlightColor?: number;
  /** 메시지 텍스트 색(기본 "#e8e8f0"). */
  readonly textColor?: string;
  /** 메시지 폰트 크기 px(기본 15). */
  readonly fontSize?: number;
  /** 메시지 박스 폭 px(기본 screenWidth - 48). */
  readonly panelWidth?: number;
  /** 텍스트 렌더 해상도 배율(기본 2 — 고DPI 선명도). */
  readonly textResolution?: number;
  /**
   * 메시지 본문 `{icon:name}` 토큰 → 게임 아이콘 사양 매핑(앱 주입 — 패키지는 게임 텍스처 키를 모름).
   * 주입되고 메시지에 `{icon:` 토큰이 있을 때만 인라인 아이콘 렌더, 아니면 기존 plain Text 경로(회귀 0).
   */
  readonly inlineIcons?: Record<string, InlineIconSpec>;
  /**
   * 손가락(포인터) 연출 텍스처 키(앱 주입 — 패키지는 게임 텍스처 키를 모름).
   * 미주입이면 gesture가 있어도 연출하지 않는다(회귀 0).
   */
  readonly pointerTextureKey?: string;
  /**
   * 텍스트 방향. 함수로 주입하면 언어 전환 뒤 새 스텝을 그릴 때 최신 방향을 반영한다.
   * LTR은 우상단, RTL은 좌상단에 자동 진행 인디케이터를 둔다.
   */
  readonly textDirection?: TutorialTextDirection | (() => TutorialTextDirection);
}

/** showStep에 넘기는 한 스텝의 렌더 사양(컨트롤러/데이터에서 Runner가 추출). */
export interface TutorialStepView {
  readonly messageKey: string;
  readonly advance: TutorialAdvanceMode;
  readonly autoAdvanceMs: number;
  readonly position: TutorialMessagePosition;
  readonly padding: number;
  readonly unifyTargets: boolean;
  /** 포인터 연출 종류(기본 none). */
  readonly gesture: TutorialGesture;
  /** drag 출발 / tap 대상(해석·보정된 Rect, 없으면 null). */
  readonly gestureFrom: TutorialRect | null;
  /** drag 도착(해석·보정된 Rect, drag일 때만). */
  readonly gestureTo: TutorialRect | null;
}

const DEFAULTS = {
  depth: 1500,
  dimColor: 0x05060f,
  dimAlpha: 0.74,
  highlightColor: 0x5ff0ff,
  textColor: "#e8e8f0",
  fontSize: 15,
  textResolution: 2,
} as const;

const AUTO_INDICATOR = {
  radius: 8,
  cornerOverlapInset: 1,
  bgColor: 0xffffff,
  bgAlpha: 0.16,
  fillColor: 0x5ff0ff,
  fillAlpha: 0.96,
} as const;

export class TutorialOverlay extends Phaser.GameObjects.Container {
  private readonly o: Required<
    Omit<
      TutorialOverlayOptions,
      | "panelStyle"
      | "fontFamily"
      | "translate"
      | "onAdvance"
      | "onSkip"
      | "skipLabelKey"
      | "safeAreaInsets"
      | "inlineIcons"
      | "pointerTextureKey"
      | "textDirection"
    >
  > &
    Pick<
      TutorialOverlayOptions,
      | "panelStyle"
      | "fontFamily"
      | "translate"
      | "onAdvance"
      | "onSkip"
      | "skipLabelKey"
      | "safeAreaInsets"
      | "inlineIcons"
    >;
  private readonly activeTweens: Phaser.Tweens.Tween[] = [];
  private readonly pointer: TutorialPointer;
  private readonly pointerTextureKey?: string;
  private readonly textDirection?: TutorialOverlayOptions["textDirection"];

  constructor(scene: Phaser.Scene, options: TutorialOverlayOptions) {
    super(scene, 0, 0);
    // 손가락 포인터는 오버레이 Container와 분리된 씬 직속 객체(depth는 오버레이 위) — removeAll과 충돌 없이 자가 정리.
    this.pointer = new TutorialPointer(scene, (options.depth ?? DEFAULTS.depth) + 1);
    this.pointerTextureKey = options.pointerTextureKey;
    this.textDirection = options.textDirection;
    this.o = {
      ...DEFAULTS,
      depth: options.depth ?? DEFAULTS.depth,
      dimColor: options.dimColor ?? DEFAULTS.dimColor,
      dimAlpha: options.dimAlpha ?? DEFAULTS.dimAlpha,
      highlightColor: options.highlightColor ?? DEFAULTS.highlightColor,
      textColor: options.textColor ?? DEFAULTS.textColor,
      fontSize: options.fontSize ?? DEFAULTS.fontSize,
      textResolution: options.textResolution ?? DEFAULTS.textResolution,
      panelWidth: options.panelWidth ?? options.screenWidth - 48,
      screenWidth: options.screenWidth,
      screenHeight: options.screenHeight,
      panelStyle: options.panelStyle,
      fontFamily: options.fontFamily,
      translate: options.translate,
      onAdvance: options.onAdvance,
      onSkip: options.onSkip,
      skipLabelKey: options.skipLabelKey ?? "tutorial.skip",
      safeAreaInsets: options.safeAreaInsets,
      inlineIcons: options.inlineIcons,
    };
    this.setDepth(this.o.depth);
    scene.add.existing(this);
  }

  /** 한 스텝 표시 — 이전 시각 요소를 모두 지우고 dim/하이라이트/메시지/입력을 다시 그린다. */
  showStep(view: TutorialStepView, rects: readonly TutorialRect[]): void {
    this.clearVisuals();
    const cutout = computeCutout(rects, view.unifyTargets, view.padding);
    let messagePanel: TutorialSkipButtonAnchorRect | null = null;
    this.drawDim(cutout, view.advance);
    if (cutout) this.drawHighlights(rects, view.padding);
    if (view.messageKey) {
      messagePanel = this.drawMessage(view.messageKey, view.position, cutout, view.autoAdvanceMs);
    }
    this.installInput(view.advance, cutout);
    this.drawSkipButton(messagePanel);
    this.pointer.show(view.gesture, view.gestureFrom, view.gestureTo, this.pointerTextureKey);
  }

  private drawDim(cutout: TutorialRect | null, _advance: TutorialAdvanceMode): void {
    const { screenWidth: W, screenHeight: H, dimColor: c, dimAlpha: a } = this.o;
    const rect = (x: number, y: number, w: number, h: number): void => {
      if (w <= 0 || h <= 0) return;
      const r = this.scene.add.rectangle(x, y, w, h, c, a).setOrigin(0, 0);
      this.add(r);
    };
    if (!cutout) {
      rect(0, 0, W, H);
      return;
    }
    // 컷아웃 둘레 4장 (상/하/좌/우).
    rect(0, 0, W, cutout.y); // 상단
    rect(0, cutout.y + cutout.height, W, H - (cutout.y + cutout.height)); // 하단
    rect(0, cutout.y, cutout.x, cutout.height); // 좌
    rect(cutout.x + cutout.width, cutout.y, W - (cutout.x + cutout.width), cutout.height); // 우
  }

  private drawHighlights(rects: readonly TutorialRect[], padding: number): void {
    for (const r of rects) {
      const x = r.x - padding;
      const y = r.y - padding;
      const w = r.width + padding * 2;
      const h = r.height + padding * 2;
      const border = this.scene.add
        .rectangle(x, y, w, h)
        .setOrigin(0, 0)
        .setStrokeStyle(2, this.o.highlightColor, 1)
        .setFillStyle();
      this.add(border);
      const tw = this.scene.tweens.add({
        targets: border,
        alpha: { from: 1, to: 0.35 },
        duration: 620,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });
      this.activeTweens.push(tw);
    }
  }

  private drawMessage(
    messageKey: string,
    position: TutorialMessagePosition,
    cutout: TutorialRect | null,
    autoAdvanceMs: number,
  ): TutorialSkipButtonAnchorRect {
    const text = this.o.translate(messageKey);
    const innerPad = 14;
    const cx = this.o.screenWidth / 2;
    const maxWidth = this.o.panelWidth - innerPad * 2;
    const textStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: this.o.fontFamily,
      fontSize: `${this.o.fontSize}px`,
      color: this.o.textColor,
      align: "center",
    };

    // `{icon:` 토큰 + inlineIcons 주입 시에만 인라인 아이콘 라벨, 아니면 기존 plain Text(회귀 0).
    let label: Phaser.GameObjects.Text | InlineIconText;
    let labelHeight: number;
    if (this.o.inlineIcons && text.includes("{icon:")) {
      const icons = this.o.inlineIcons;
      const inline = new InlineIconText(this.scene, cx, 0, text, {
        maxWidth,
        textStyle,
        align: "center",
        resolution: this.o.textResolution,
        resolveIcon: (name) => icons[name],
      });
      label = inline;
      labelHeight = inline.contentHeight;
    } else {
      const plain = this.scene.add
        .text(cx, 0, text, { ...textStyle, wordWrap: { width: maxWidth } })
        .setOrigin(0.5)
        .setResolution(this.o.textResolution);
      label = plain;
      labelHeight = plain.height;
    }

    const panelH = Math.max(44, labelHeight + 24);
    const panelY = computeMessagePanelY(position, panelH, cutout, this.o.screenHeight);

    const panel = new NineSlicePanel(this.scene, cx, panelY, {
      style: this.o.panelStyle,
      width: this.o.panelWidth,
      height: panelH,
      originX: 0.5,
      originY: 0.5,
    });
    this.add(panel);
    label.setPosition(cx, panelY);
    this.add(label);

    if (autoAdvanceMs > 0) {
      this.drawAutoAdvanceIndicator(panelY, panelH, autoAdvanceMs);
    }
    return {
      x: cx - this.o.panelWidth / 2,
      y: panelY - panelH / 2,
      width: this.o.panelWidth,
      height: panelH,
    };
  }

  private drawAutoAdvanceIndicator(panelY: number, panelH: number, durationMs: number): void {
    const { radius, cornerOverlapInset, bgColor, bgAlpha, fillColor, fillAlpha } = AUTO_INDICATOR;
    const dir = resolveTextDirection(this.textDirection);
    const panelLeft = this.o.screenWidth / 2 - this.o.panelWidth / 2;
    const panelRight = this.o.screenWidth / 2 + this.o.panelWidth / 2;
    const panelTop = panelY - panelH / 2;
    const x = dir === "rtl" ? panelLeft + cornerOverlapInset : panelRight - cornerOverlapInset;
    const y = panelTop + cornerOverlapInset;
    const bg = this.scene.add.circle(x, y, radius, bgColor, bgAlpha).setStrokeStyle(1, bgColor, 0.34);
    const fill = this.scene.add.graphics();
    const progress = { value: 1 };
    const redraw = (): void => {
      fill.clear();
      if (progress.value <= 0) return;
      const topAngle = -Math.PI / 2;
      const missingAngle = Math.PI * 2 * (1 - progress.value);
      const clockwise = dir !== "rtl";
      const startAngle = clockwise ? topAngle + missingAngle : topAngle - missingAngle;
      const endAngle = clockwise ? topAngle + Math.PI * 2 : topAngle - Math.PI * 2;
      fill.fillStyle(fillColor, fillAlpha);
      fill.beginPath();
      fill.moveTo(x, y);
      fill.arc(x, y, Math.max(1, radius - 1), startAngle, endAngle, !clockwise);
      fill.closePath();
      fill.fillPath();
    };

    redraw();
    this.add(bg);
    this.add(fill);
    const tw = this.scene.tweens.add({
      targets: progress,
      value: 0,
      duration: durationMs,
      ease: "Linear",
      onUpdate: redraw,
      onComplete: redraw,
    });
    this.activeTweens.push(tw);
  }

  private installInput(advance: TutorialAdvanceMode, cutout: TutorialRect | null): void {
    // action 모드 + 컷아웃 존재 → 전면 차단 없이 컷아웃(실제 UI)만 열어두고 dim만 흡수.
    if (advance === "action" && cutout) {
      this.addDimSwallowZones(cutout);
      return;
    }
    // tap / spotlightTap (또는 컷아웃 없는 action → 소프트락 방지로 tap처럼) → 전면 Zone.
    // 설치 순간 손가락이 눌려 있으면(탭 진입 pointerdown 제스처 진행 중) 그 제스처의 첫 떼기를
    // 무시한다 — 그 클릭이 방금 띄운 오버레이를 즉시 닫는 "찰나 사라짐" 차단(시계 비교 대신 상태 직접).
    const gate = createEnteringGestureGate(this.scene.input.activePointer.isDown);
    const zone = this.scene.add
      .zone(0, 0, this.o.screenWidth, this.o.screenHeight)
      .setOrigin(0, 0)
      .setInteractive();
    zone.on("pointerup", (pointer: Phaser.Input.Pointer) => {
      // gate는 spotlightTap 좌표 검증보다 먼저 — 진입 제스처는 spotlight 안팎 무관하게 1회 무시해야 함.
      if (!gate.shouldAdvance()) return; // 진입 제스처 잔류 떼기 — 무시
      if (advance === "spotlightTap" && cutout && !pointInRect(pointer.x, pointer.y, cutout)) return;
      this.o.onAdvance();
    });
    this.add(zone);
  }

  private drawSkipButton(messagePanel: TutorialSkipButtonAnchorRect | null): void {
    if (!this.o.onSkip) return;
    drawTutorialSkipButton(this, {
      screenWidth: this.o.screenWidth,
      screenHeight: this.o.screenHeight,
      fontFamily: this.o.fontFamily,
      fontSize: this.o.fontSize,
      textColor: this.o.textColor,
      textResolution: this.o.textResolution,
      translate: this.o.translate,
      labelKey: this.o.skipLabelKey ?? "tutorial.skip",
      safeAreaInsets: this.o.safeAreaInsets,
      messagePanel,
      onSkip: this.o.onSkip,
    });
  }

  /** 컷아웃 둘레 4영역만 탭을 흡수(컷아웃 자체는 비워 실제 UI 입력 통과). */
  private addDimSwallowZones(cutout: TutorialRect): void {
    const { screenWidth: W, screenHeight: H } = this.o;
    const swallow = (x: number, y: number, w: number, h: number): void => {
      if (w <= 0 || h <= 0) return;
      const z = this.scene.add.zone(x, y, w, h).setOrigin(0, 0).setInteractive();
      z.on("pointerup", () => {
        /* 흡수: dim 영역 탭은 아무 동작 없음 */
      });
      this.add(z);
    };
    swallow(0, 0, W, cutout.y);
    swallow(0, cutout.y + cutout.height, W, H - (cutout.y + cutout.height));
    swallow(0, cutout.y, cutout.x, cutout.height);
    swallow(cutout.x + cutout.width, cutout.y, W - (cutout.x + cutout.width), cutout.height);
  }

  private clearVisuals(): void {
    for (const tw of this.activeTweens) tw.remove();
    this.activeTweens.length = 0;
    this.pointer.clear(); // 손가락(씬 직속)은 Container 자식이 아니라 직접 정리
    this.removeAll(true); // 자식 destroy
  }

  override destroy(fromScene?: boolean): void {
    for (const tw of this.activeTweens) tw.remove();
    this.activeTweens.length = 0;
    this.pointer.clear();
    super.destroy(fromScene);
  }
}
